/**
 * @module useBank
 * @category Hooks
 * @description A custom hook that provides access to a bank of data items and the ability to update them in-place.
 *
 * @typeparam T - The type of data items stored in the bank.
 * @returns A `Bank` object.
 * */
export default function useBank<T extends IBanklet>(): Bank<T> {
    const bank = new Bank<T>();
    return bank;
}

/**
 * @module useBank
 * @category Hooks
 * @description A class that provides access to a bank of data items and the ability to update them in-place.
 *
 * Represents an object that can be stored in a `Bank`.
 * Classes implementing this interface should provide a method to compare instances for equality.
 */
export interface IBanklet {
    /** Returns true if the two objects are the same.
     *  This is used to determine if an object should be updated in the bank.
     *  note that what is used should be unique to the object.
     *  For example, if the object is a user, the user's id should be used.
     *
     *  @param other The object to compare to.
     *  @returns True if the two objects are the same.
     *
     */
    amI: (other: IBanklet) => boolean;
}

/**
 * @module useBank
 * @category Hooks
 *
 * @description Represents a generic bank of data items that can be updated in-place.
 * This bank is designed to store objects that implement the `IBanklet` interface.
 * The `updateData` method allows updating existing items based on a unique identifier.
 *
 * @typeparam T - The type of data items stored in the bank.
 */
export class Bank<T extends IBanklet> {
    private _data: Array<T>;

    /**
     * Initializes a new instance of the `Bank` class.
     */
    constructor() {
        console.log("Bank constructor");
        this._data = [] as Array<T>;
    }

    /**
     *  Gets the data items stored in the bank.
     */
    public get(): Array<T> {
        return this._data;
    }

    /**
     * Updates an existing data item in the bank based on its unique identifier.
     * If a matching item is found, it is replaced with the provided item, and a callback is invoked.
     *
     * @param datum - The updated data item to be matched and replaced.
     * @param callback - A callback function that is invoked after the item is updated.
     */
    public update(datum: T, callback: (data: T) => void) {
        for (let i = 0; i < this._data.length; i++) {
            if (datum.amI(this._data[i])) {
                this._data[i] = datum;
                callback(this._data[i]);
                break;
            }
        }
        return void 0;
    }

    /**
     * @description Adds a new data item to the bank.
     *
     * @param datum - The data item to be added.
     *
     * @remarks
     * This method does not check for duplicates.
     * It is the responsibility of the caller to ensure that the item is unique.
     * It is useful that in the creation of a new item, the unique identifier is created.
     * before the item is added to the bank.
     * @see {@link update}
     */
    public add(datum: T) {
        console.log("Adding data to bank");
        this._data.push(datum);
    }

    /**
     * @description Removes all data items from the bank.
     */
    public destroy() {
        this._data = [];
    }
}
